import { useCallback, useState } from 'react';
import {
    markChatAsSeen,
    markChatAsUnread,
    setChatTeam,
    assignChatUser,
    unassignChatUser
} from '../services/chat';
import { conversationRowKey, type ConversationRowRef } from '../services/conversations';
import type { ToastType } from '../providers/ToastProvider';

export function useConversationActions(
    refreshConversations: () => Promise<void>,
    showToast: (msg: string, type: ToastType) => void
) {
    const [markingSeenKey, setMarkingSeenKey] = useState<string | null>(null);
    const [markingUnreadKey, setMarkingUnreadKey] = useState<string | null>(null);

    const handleMarkAsSeen = useCallback(
        async (e: React.MouseEvent, row: ConversationRowRef) => {
            e.stopPropagation();
            setMarkingSeenKey(conversationRowKey(row));
            try {
                await markChatAsSeen(row.patientId, row.status);
                await refreshConversations();
            } catch {
                showToast('Failed to mark as seen', 'error');
            } finally {
                setMarkingSeenKey(null);
            }
        },
        [refreshConversations, showToast]
    );

    const handleMarkAsUnread = useCallback(
        async (e: React.MouseEvent, row: ConversationRowRef) => {
            e.stopPropagation();
            setMarkingUnreadKey(conversationRowKey(row));
            try {
                await markChatAsUnread(row.patientId, row.status);
                await refreshConversations();
            } catch {
                showToast('Failed to mark as unread', 'error');
            } finally {
                setMarkingUnreadKey(null);
            }
        },
        [refreshConversations, showToast]
    );

    // Team/assignment are per thread: each row's status picks the ODO viewer row
    // the write targets, so an internal row never mutates its external twin.
    const handleTeamChange = useCallback(
        async (row: ConversationRowRef, team: string) => {
            try {
                await setChatTeam(row.patientId, team, row.status);
                await refreshConversations();
            } catch {
                showToast('Failed to update team', 'error');
            }
        },
        [refreshConversations, showToast]
    );

    const handleAssign = useCallback(
        async (row: ConversationRowRef, email: string, currentAssignedTo: string | null) => {
            try {
                await assignChatUser(row.patientId, email, currentAssignedTo, row.status);
                await refreshConversations();
            } catch (err: unknown) {
                const isConflict =
                    (err as { response?: { status?: number } })?.response?.status === 409;
                showToast(
                    isConflict
                        ? 'Someone else just changed this assignment — refreshing'
                        : 'Failed to assign user',
                    'error'
                );
                if (isConflict) await refreshConversations();
            }
        },
        [refreshConversations, showToast]
    );

    const handleUnassign = useCallback(
        async (row: ConversationRowRef, currentAssignedTo: string | null) => {
            try {
                await unassignChatUser(row.patientId, currentAssignedTo, row.status);
                await refreshConversations();
            } catch (err: unknown) {
                const isConflict =
                    (err as { response?: { status?: number } })?.response?.status === 409;
                showToast(
                    isConflict
                        ? 'Someone else just changed this assignment — refreshing'
                        : 'Failed to unassign user',
                    'error'
                );
                if (isConflict) await refreshConversations();
            }
        },
        [refreshConversations, showToast]
    );

    return {
        markingSeenKey,
        markingUnreadKey,
        handleMarkAsSeen,
        handleMarkAsUnread,
        handleTeamChange,
        handleAssign,
        handleUnassign
    };
}
